import { supabase } from '@/integrations/supabase/client';
import { Invoice, InvoiceStatus } from '@/types/invoice';
import { settingsService } from './settingsService';

type InvoiceRecord = {
  id: string;
  invoice_number: string;
  quote_id?: string | null;
  client_info: Invoice['clientInfo'];
  items: Invoice['items'];
  financials: Invoice['financials'];
  status: string;
  issued_at?: string | null;
  due_at?: string | null;
  gst_number?: string | null;
  description?: string | null;
  notes?: string | null;
  terms_info?: string[] | null;
  is_locked?: boolean | null;
  created_at: string;
  branding_snapshot?: any;
  payment_schedule_id?: string | null;
  archived_at?: string | null;
  archived_by?: string | null;
  archive_reason?: string | null;
};

export const invoiceService = {
  async fetchInvoices() {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .is('archived_at', null)
      .order('created_at', { ascending: false });

    if (error) {
      // Provide actionable details in UI/toasts (Supabase often returns useful `details`/`hint`)
      const extra = [error.code, error.details, error.hint].filter(Boolean).join(' | ');
      const message = extra ? `${error.message} (${extra})` : error.message;
      throw new Error(message);
    }

    return (data || []).map(this.mapDbToInvoice);
  },

  async createInvoice(invoice: Partial<Invoice>) {
    // Guard: prevent creating an invoice against a draft or unapproved quote
    if (invoice.quoteId) {
      const { data: quote, error: quoteErr } = await supabase
        .from('quotations')
        .select('status')
        .eq('id', invoice.quoteId)
        .single();
      if (quoteErr) throw new Error('Could not verify quote status before invoicing');
      if (quote && quote.status === 'draft') {
        throw new Error(
          'Cannot create an invoice for a draft quotation. ' +
          'Please approve the quote first before raising an invoice.'
        );
      }
    }

    const dbInvoice = this.mapInvoiceToDb(invoice);
    const { data, error } = await supabase
      .from('invoices')
      .insert(dbInvoice)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
         throw new Error('An active invoice already exists for this billing target.');
      }
      throw error;
    }
    return this.mapDbToInvoice(data);
  },

  async updateInvoice(id: string, updates: Partial<Invoice>) {
    const dbUpdates = this.mapInvoiceToDb(updates);
    const { data, error } = await supabase
      .from('invoices')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return this.mapDbToInvoice(data);
  },

  async archiveInvoice(id: string, reason?: string) {
    const { data: session } = await supabase.auth.getSession();
    const userId = session?.session?.user?.id;

    const { data, error } = await supabase
      .from('invoices')
      .update({ 
        archived_at: new Date().toISOString(),
        archived_by: userId || null,
        archive_reason: reason || 'Archived by user'
      })
      .eq('id', id)
      .in('status', ['draft', 'cancelled']) // Safety: only allow archiving drafts and cancelled invoices
      .select()
      .single();

    if (error) throw error;
    return this.mapDbToInvoice(data);
  },

  async finalizeInvoice(id: string): Promise<string> {
    const { data: tenantId } = await supabase.rpc('current_tenant_id');
    const { data, error } = await supabase.rpc('fn_finalize_invoice', {
      p_invoice_id: id,
      p_tenant_id: tenantId
    });

    if (error) {
      console.error('[InvoiceService] Finalize error:', error);
      throw error;
    }

    return (data as any).invoice_id;
  },

  mapDbToInvoice(db: InvoiceRecord): Invoice {
    return {
      id: db.id,
      invoiceNumber: db.invoice_number,
      quoteId: db.quote_id,
      clientInfo: db.client_info,
      items: db.items,
      financials: db.financials,
      status: db.status as InvoiceStatus,
      issuedAt: db.issued_at,
      dueAt: db.due_at,
      gstNumber: db.gst_number,
      description: db.description,
      notes: db.notes,
      terms: db.terms_info || [],
      isLocked: db.is_locked,
      createdAt: db.created_at,
      branding_snapshot: db.branding_snapshot || undefined,
      paymentScheduleId: db.payment_schedule_id || undefined,
    };
  },

  mapInvoiceToDb(invoice: Partial<Invoice>) {
    const isLocked = invoice.status && invoice.status !== 'draft';

    return {
      invoice_number: invoice.invoiceNumber,
      quote_id: invoice.quoteId,
      client_info: invoice.clientInfo,
      items: invoice.items,
      financials: invoice.financials,
      status: invoice.status,
      issued_at: invoice.issuedAt,
      due_at: invoice.dueAt,
      gst_number: invoice.gstNumber,
      description: invoice.description,
      notes: invoice.notes,
      terms_info: invoice.terms,
      is_locked: invoice.isLocked ?? isLocked,
      branding_snapshot: invoice.branding_snapshot,
      payment_schedule_id: invoice.paymentScheduleId,
    };
  },

  async getNextInvoiceNumber(fyPrefix: string): Promise<string> {
    const { data: tenantId } = await supabase.rpc('current_tenant_id');
    const { data, error } = await supabase.rpc('fn_next_invoice_number', { p_tenant_id: tenantId });
    if (error) {
      console.error('Error fetching sequential invoice number:', error);
      // Fallback: timestamp-based to reduce collision risk
      const ts = Date.now().toString().slice(-6);
      const prefix = settingsService.getPreferences().quote_number_prefix || 'INV';
      return `${prefix}/INV/${fyPrefix}/${ts}`;
    }
    return data;
  },

  getFyPrefix() {
    const date = new Date();
    const currentYear = date.getFullYear();
    const month = date.getMonth();
    // Fiscal year starts in April in India
    const fyStart = month >= 3 ? currentYear : currentYear - 1;
    const fyEnd = (fyStart + 1).toString().slice(-2);
    return `${fyStart.toString().slice(-2)}-${fyEnd}`;
  },

  generateInvoiceNumber() {
    return this.getFyPrefix(); // Backward compatible if only needing the FY part
  },

  buildDraftInvoiceNumber(prefix?: string) {
    const fyPrefix = this.getFyPrefix();
    const draftSuffix = Math.floor(1000 + Math.random() * 9000);
    return `${prefix || settingsService.getPreferences().quote_number_prefix || 'INV'}/DRAFT/${fyPrefix}/${draftSuffix}`;
  },

  isDraftInvoiceNumber(invoiceNumber?: string | null) {
    return Boolean(invoiceNumber && invoiceNumber.includes('/DRAFT/'));
  }
};
